import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OwnerAlertCard } from "@/components/OwnerAlertCard";
import { PageHeader } from "../components/PageHeader";

const ALERT_LIMIT = 100;

const statusFilters = [
  { value: "unread", label: "Da leggere" },
  { value: "read", label: "Letti" },
  { value: "resolved", label: "Risolti" },
  { value: "all", label: "Tutti" },
];

function getLoadErrorMessage(error) {
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  return "Impossibile caricare gli avvisi.";
}

function getActionErrorMessage(error) {
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  if (error?.response?.status === 404) {
    return "Avviso non più disponibile.";
  }
  return "Impossibile aggiornare l'avviso.";
}

export function OwnerAlertsPage() {
  const outletContext = useOutletContext() ?? {};
  const { refreshOwnerAlertsUnreadCount } = outletContext;
  const [status, setStatus] = useState("unread");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState("");

  const loadAlerts = useCallback(async (nextStatus = status) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/owner-alerts", {
        params: {
          status: nextStatus,
          limit: ALERT_LIMIT,
        },
      });
      setAlerts(response.data);
    } catch (loadError) {
      const message = getLoadErrorMessage(loadError);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadAlerts(status);
  }, [loadAlerts, status]);

  const handleStatusChange = (nextStatus) => {
    setStatus(nextStatus);
  };

  const markRead = async (alertId) => {
    setActionLoading(alertId);
    try {
      await api.post(`/owner-alerts/${alertId}/read`);
      await loadAlerts(status);
      await refreshOwnerAlertsUnreadCount?.();
    } catch (actionError) {
      toast.error(getActionErrorMessage(actionError));
    } finally {
      setActionLoading("");
    }
  };

  const resolveAlert = async (alertId) => {
    setActionLoading(alertId);
    try {
      await api.post(`/owner-alerts/${alertId}/resolve`);
      await loadAlerts(status);
      await refreshOwnerAlertsUnreadCount?.();
    } catch (actionError) {
      toast.error(getActionErrorMessage(actionError));
    } finally {
      setActionLoading("");
    }
  };

  const isUnreadEmpty = status === "unread" && alerts.length === 0;

  return (
    <div className="space-y-6" data-testid="owner-alerts-page">
      <PageHeader
        title="Avvisi operativi"
        subtitle="Controlla gli eventi che richiedono una tua verifica o azione."
        titleTestId="owner-alerts-title"
        subtitleTestId="owner-alerts-subtitle"
      />

      <section className="bg-white border border-slate-200 rounded-sm p-3" data-testid="owner-alerts-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => handleStatusChange(filter.value)}
              className={`rounded-sm px-3 py-2 text-sm font-medium border ${
                status === filter.value
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
              data-testid={`owner-alerts-filter-${filter.value}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </section>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 text-sm text-slate-500" data-testid="owner-alerts-loading">
          Caricamento avvisi...
        </div>
      )}

      {!loading && error && (
        <div className="bg-white border border-red-200 rounded-sm p-6 text-sm text-red-600" data-testid="owner-alerts-error">
          {error}
        </div>
      )}

      {!loading && !error && alerts.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="owner-alerts-empty">
          <h3 className="text-base font-semibold section-title">
            {isUnreadEmpty ? "Tutto sotto controllo" : "Nessun avviso trovato"}
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            {isUnreadEmpty
              ? "Non ci sono avvisi che richiedono la tua attenzione immediata."
              : "Nessun avviso trovato per questo filtro."}
          </p>
        </div>
      )}

      {!loading && !error && alerts.length > 0 && (
        <section className="space-y-3" data-testid="owner-alerts-list">
          {alerts.map((alert) => (
            <OwnerAlertCard
              key={alert.id}
              alert={alert}
              actionLoading={actionLoading}
              onMarkRead={markRead}
              onResolve={resolveAlert}
            />
          ))}
          {alerts.length >= ALERT_LIMIT && (
            <p className="text-xs text-slate-400" data-testid="owner-alerts-limit-notice">
              Stai vedendo gli avvisi più recenti.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
